// Internal dependencies
import Translate from '@/filters/translate'
import stateNames from '@/core/states'

// Components
import CheckoutCart from '@/components/checkout-cart/component'
import StripeButton from '@/components/stripe-button/component'
import DropDown from '@/components/dropdown/component'
import DropDownPhonePrefix from '@/components/dropdown-phone-prefix/component'

// Mixins
import CheckoutMixin from '@/mixins/checkoutMixin'

import countriesLib from 'i18n-iso-countries'
import LibPhoneNumber from 'google-libphonenumber'

countriesLib.registerLocale(require('i18n-iso-countries/langs/en.json'))
countriesLib.registerLocale(require('i18n-iso-countries/langs/sv.json'))

let stripe
let card

export default {
  name: 'checkout-page',
  components: { CheckoutCart, StripeButton, DropDown, DropDownPhonePrefix },
  mixins: [CheckoutMixin],

  data () {
    return {
      // Form models.
      // voucherCode is a local value instead of a computed store value, since the store will hold the value accepted
      // by, and loaded by, the server. Therefore, we'll treat the form as simply the user input, and the store value
      // as an accepted coupon.
      //
      // Using the store value everywhere would mean that if you change the voucher code in the field, the input value
      // would be displayed in the cart etc.
      voucherCode: '',
      isAddingVoucher: false,
      voucherAddFailed: false,

      giftCardCode: '',
      isAddingGiftCard: false,
      giftCardAddFailed: false,

      // Phone number regions. Default to Sweden.
      // {
      //  "region": "SE",
      //  "prefix": "+46"
      // }
      phoneNumberRegion: {
        region: 'SE',
        prefix: '+46'
      },
      deliveryPhoneNumberRegion: {
        region: 'SE',
        prefix: '+46'
      },

      // Component data.
      loaded: false,
      voucherFormHeight: 'auto',
      voucherFormNaturalHeight: '0px',
      countrySelectIsShowing: false,
      separateDeliveryAddressHeight: 'auto',
      separateDeliveryAddressNaturalHeight: 'auto',
      errorMessage: '',

      // Stripe.
      stripeError: '',
      cardToken: '',
      qliroSnippet: ''
    }
  },

  mounted () {
    // Find out the natural height of the voucher form for smooth animation.
    this.voucherFormNaturalHeight = this.$el.querySelector('.voucher-container').offsetHeight + 'px'
    this.voucherFormHeight = '0px'

    // If we already have a voucher loaded and it is valid, set it in the form.
    if (this.$store.getters.voucher.isValid === true) {
      this.voucherCode = this.$store.getters.voucher.code
    } else {
      this.$store.dispatch('clearVoucher')
      this.voucherCode = ''
    }

    // If we already have a gift card loaded and it is valid, set it in the form.
    if (this.$store.getters.giftCard.isValid === true) {
      this.giftCardCode = this.$store.getters.giftCard.code
    } else {
      this.$store.dispatch('clearGiftCard')
      this.giftCardCode = ''
    }

    this.$store.dispatch('trackCheckout', this.$store.getters.cartItems)
    if (this.isQliroEnabled) {
      if (this.$store.getters.orderId !== '') {
        this.$store.dispatch('setAllowClosingBackdrop', false)
        this.$store.dispatch('setOrderState', 'updatingOrder')
        this.$store.dispatch('setBackdropMessage', Translate.translate(this.$store.getters.orderState))
        this.$store.dispatch('showBackdrop', true)
        let self = this
        this.updateOrderPromise().then(function (result) {
          self.qliroSnippet = result[1].body.OrderHtmlSnippet
          self.$nextTick(() => {
            self.initQliroDiv()
            self.$store.dispatch('showBackdrop', false)
          })
        }).catch(function () {
          self.$store.dispatch('clearCheckoutSoft', true)
          self.shippingMethod = 'standard'
          self.confirmOrder()
        })
      } else {
        this.$store.dispatch('clearCheckoutSoft', true)
        this.shippingMethod = 'standard'
        this.confirmOrder()
      }
    } else {
      // Find out the natural height of the separate delivery address container for smooth animation.
      this.separateDeliveryAddressNaturalHeight = this.$el.querySelector('.separate-delivery-address-container').offsetHeight + 'px'
      this.separateDeliveryAddressHeight = '0px'
      this.setupStripeForm()

      if (this.isCountrySet === false) {
        this.$store.dispatch('getCountryCode').then(function (response) {
          const countryCode = response.toUpperCase()
          const prefix = '+' + LibPhoneNumber.PhoneNumberUtil.getInstance().getCountryCodeForRegion(countryCode)
          self.setCountry({ value: countryCode })
          self.setDeliveryCountry({ value: countryCode })
          self.regionSelected({ region: countryCode, prefix: prefix })
          self.deliveryRegionSelected({ region: countryCode, prefix: prefix })
          self.isCountrySet = true
        }).catch(function (err) {
          console.log(err)
          self.isCountrySet = true
        })
      }

      // We'll need a timeout function before restoring transitions, or the browser will start running them before we've
      // registered that the component has been mounted.
      let self = this
      setTimeout(function () {
        self.loaded = true
        self.$store.dispatch('showBackdrop', false)
      }, 50)
    }
  },

  updated () {
    this.separateDeliveryAddressHeight = this.$store.getters.checkoutSeparateDeliveryAddress ? this.separateDeliveryAddressNaturalHeight : '0px'
  },

  watch: {
    '$store.getters.cartItems': {
      handler: function () {
        if (this.isQliroEnabled) {
          this.updateOrder()
        }
      },
      deep: true
    }
  },

  computed: {

    phoneMobileRegion: {
      set (value) { this.$store.dispatch('setCheckoutPhoneMobileRegion', value) },
      get () { return this.$store.getters.checkoutPhoneMobileRegion }
    },

    phoneMobileIsValid () {
      return this.$store.getters.checkoutPhoneMobileIsValid
    },

    countries: {
      set (value) { this.$store.dispatch('setCheckoutCountry', value) },
      get () {
        let lang = this.$store.getters.getCurrLanguage
        let countryNames = countriesLib.getNames(lang.iso)
        let countries = Object.keys(countryNames).map(countryCode => ({
          name: countryNames[countryCode],
          value: countryCode
        }))
        let selectedCountryISO = this.$store.getters.checkoutCountry
        countries.forEach(function (item) {
          if (item.value === selectedCountryISO) {
            item.preSelected = true
          }
        })
        return countries
      }
    },

    states: {
      set (value) { this.$store.dispatch('setCheckoutState', value) },
      get () {
        const ret = []
        Object.keys(stateNames).forEach((key) => {
          ret.push({
            name: stateNames[key],
            value: key,
            preSelected: key === this.$store.getters.checkoutState
          })
        })
        return ret
      }
    },

    separateDeliveryAddress: {
      set (value) { this.$store.dispatch('setCheckoutSeparateDeliveryAddress', value) },
      get () { return this.$store.getters.checkoutSeparateDeliveryAddress }
    },

    rememberMe: {
      set (value) { this.$store.dispatch('setCheckoutRememberMe', value) },
      get () { return this.$store.getters.checkoutRememberMe }
    },

    deliveryPhoneMobileRegion: {
      set (value) { this.$store.dispatch('setCheckoutDeliveryPhoneMobileRegion', value) },
      get () { return this.$store.getters.checkoutDeliveryPhoneMobileRegion }
    },

    deliveryPhoneMobileIsValid () {
      return this.$store.getters.checkoutDeliveryPhoneMobileIsValid
    },

    deliveryCountries: {
      set (value) { this.$store.dispatch('setCheckoutDeliveryCountry', value) },
      get () {
        let lang = this.$store.getters.getCurrLanguage
        let countryNames = countriesLib.getNames(lang.iso)
        let countries = Object.keys(countryNames).map(countryCode => ({
          name: countryNames[countryCode],
          value: countryCode
        }))
        let selectedCountryISO = this.$store.getters.checkoutDeliveryCountry
        countries.forEach(function (item) {
          if (item.value === selectedCountryISO) {
            item.preSelected = true
          }
        })
        return countries
      }
    },

    deliveryStates: {
      set (value) { this.$store.dispatch('setCheckoutDeliveryState', value) },
      get () {
        const ret = []
        Object.keys(stateNames).forEach((key) => {
          ret.push({
            name: stateNames[key],
            value: key,
            preSelected: key === this.$store.getters.checkoutDeliveryState
          })
        })
        return ret
      }
    },

    // paymentRequestDisplayItems returns items to be used in the Stripe integration.
    paymentRequestDisplayItems () {
      let items = []
      this.$store.getters.cartItems.forEach(function (cartItem) {
        items.push({
          label: cartItem.product ? cartItem.product.name : cartItem.productName,
          amount: cartItem.count * cartItem.price
        })
      })

      return items
    },

    // Validators
    emailIsValid () {
      return this.$store.getters.checkoutEmailIsValid
    },

    deliveryEmailIsValid () {
      return this.$store.getters.checkoutDeliveryEmailIsValid
    },

    framesWithoutPoster () {
      let posterExists = false
      let frameExists = false
      this.$store.getters.cartItems.forEach(function (cartItem) {
        if (cartItem.product) {
          if (cartItem.product.category === 'frame' || cartItem.product.category === 'hanger') {
            frameExists = true
          }
          if (cartItem.product.category === 'poster') {
            posterExists = true
          }
        } else {
          posterExists = true
        }
      })
      return posterExists === false && frameExists === true
    },

    giftCardsInCart () {
      let ret = false
      this.$store.getters.cartItems.forEach(function (cartItem) {
        if (cartItem.product && cartItem.product.category === 'giftCard') {
          ret = true
        }
      })
      return ret
    },

    onlyGiftCardsInCart () {
      return this.$store.getters.onlyGiftCardsInCart
    },

    confirmOrderDisabled () {
      if (this.isQliroEnabled) {
        return this.framesWithoutPoster || this.$store.getters.cartItems.length < 1
      } else {
        if (this.$store.getters.checkoutSeparateDeliveryAddress) {
          return this.framesWithoutPoster || !this.emailIsValid ||
            !this.phoneMobileIsValid ||
            this.firstName.length < 1 ||
            this.lastName.length < 1 ||
            this.address.length < 1 ||
            this.postalCode.length < 1 ||
            this.city.length < 1 ||
            !this.deliveryEmailIsValid ||
            !this.deliveryPhoneMobileIsValid ||
            this.deliveryFirstName.length < 1 ||
            this.deliveryLastName.length < 1 ||
            this.deliveryAddress.length < 1 ||
            this.deliveryPostalCode.length < 1 ||
            this.deliveryCity.length < 1
        }
        return this.framesWithoutPoster || !this.emailIsValid ||
          !this.phoneMobileIsValid ||
          this.firstName.length < 1 ||
          this.lastName.length < 1 ||
          this.address.length < 1 ||
          this.postalCode.length < 1 ||
          this.city.length < 1
      }
    },

    isCountrySet: {
      set (value) { this.$store.dispatch('setIsCountrySet', value) },
      get () { return this.$store.getters.isCountrySet }
    }
  },

  methods: {
    addVoucher () {
      if (this.isAddingVoucher) {
        return
      }
      this.isAddingVoucher = true

      let self = this
      this.$store.dispatch('addVoucher', this.voucherCode).then(function (response) {
        self.isAddingVoucher = false
        self.voucherAddFailed = false

        if (self.isQliroEnabled) {
          self.updateOrder()
        }
      }).catch(function (response) {
        self.isAddingVoucher = false
        self.voucherAddFailed = true

        if (self.isQliroEnabled) {
          self.updateOrder()
        }
      })
    },

    addGiftCard () {
      if (this.isAddingGiftCard) {
        return
      }
      this.isAddingGiftCard = true

      let self = this
      this.$store.dispatch('addGiftCard', this.giftCardCode).then(function (response) {
        self.isAddingGiftCard = false
        self.giftCardAddFailed = false

        if (self.isQliroEnabled) {
          self.updateOrder()
        }
      }).catch(function (response) {
        self.isAddingGiftCard = false
        self.giftCardAddFailed = true

        if (self.isQliroEnabled) {
          self.updateOrder()
        }
      })
    },

    // Setup the stripe card form, accepting card details. Note that Stripe doesn't have an NPM package for client JS.
    // The Stripe library is included in index.html, which allows us to use the global Stripe function.
    setupStripeForm () {
      // Stripe is declared in index.html. As Stripe doesn't have an NPM package, we have to include the library by
      // direct import from them.
      /* eslint-disable no-undef */
      stripe = Stripe(process.env.STRIPE_PUBLISHABLE_KEY)
      card = stripe.elements().create('card', {
        hidePostalCode: true,
        style: {
          base: {
            fontFamily: '"nunito-sans", sans-serif',
            fontSize: '.95em',
            fontSmoothing: 'antialiased',
            '::placeholder': {
              color: '#8c9199'
            }
          },
          invalid: {
            color: '#fa755a',
            iconColor: '#fa755a'
          }
        }
      })
      card.mount('#card-element')
      // Monitor change events on the Card Element to display any errors.
      card.on('change', function (error) {
        const cardErrors = document.getElementById('card-errors')
        if (error) {
          cardErrors.textContent = error.message
          cardErrors.style.display = ''
        } else {
          cardErrors.style.display = 'none'
        }
      })
    },

    // toggleVoucherForm will show the voucher form if it's hidden, or hide it if it's visible. The use of '0px' is used
    // for readability, clarity and consistency in the code.
    toggleVoucherForm () {
      if (this.voucherFormHeight === '0px') {
        this.voucherFormHeight = 'auto'
      } else {
        this.voucherFormHeight = '0px'
      }
    },

    setCountry (country) {
      this.$store.dispatch('setCheckoutCountry', country.value)
      this.$store.dispatch('setCheckoutState', '')
    },

    setState (state) {
      this.$store.dispatch('setCheckoutState', state.value)
    },

    setDeliveryCountry (country) {
      this.$store.dispatch('setCheckoutDeliveryCountry', country.value)
      this.$store.dispatch('setCheckoutDeliveryState', '')
    },

    setDeliveryState (state) {
      this.$store.dispatch('setCheckoutDeliveryState', state.value)
    },

    toggleSeparateDeliveryAddress () {
      let self = this
      if (this.separateDeliveryAddressHeight === '0px') {
        self.separateDeliveryAddressHeight = self.separateDeliveryAddressNaturalHeight
        self.$el.querySelector('.separate-delivery-address-container').classList.add('is-visible')
        self.$store.dispatch('setCheckoutSeparateDeliveryAddress', true)
      } else {
        this.separateDeliveryAddressHeight = '0px'
        this.$el.querySelector('.separate-delivery-address-container').classList.remove('is-visible')
        self.$store.dispatch('setCheckoutSeparateDeliveryAddress', false)
      }
    },

    regionSelected (region) {
      this.phoneMobileRegion = region
    },

    deliveryRegionSelected (region) {
      this.deliveryPhoneMobileRegion = region
    },

    // confirmOrder is the handler for what happens when the customer clicks the "Confirm"-button.
    // Cards to trigger errors can be found here:
    // @see https://stripe.com/docs/testing
    confirmOrder () {
      // Make sure the user can't close the page without confirming.
      window.onunload = function () {
        return confirm(Translate.translate('onunloadConfirmation'))
      }

      // Reset error message.
      this.errorMessage = ''

      // Prepare objects we'll re-use.
      let self = this
      let order = {}

      // Set backdrop message etc.
      this.$store.dispatch('setAllowClosingBackdrop', false)
      this.$store.dispatch('setBackdropMessage', Translate.translate(this.$store.getters.orderState))
      this.$store.dispatch('showBackdrop', true)

      // Perform checkout flow.
      // @todo Rework and move to store.
      this.$store.dispatch('setOrderState', 'creatingOrder')
      this.$store.dispatch('createOrder').then(function (response) {
        order = response.body

        // Store the first order state and keep track of the order number.
        self.$store.dispatch('setOrderId', order.id)
        self.$store.dispatch('setOrderConfirmationNumber', order.referenceNumber)
        self.$store.dispatch('setOrderState', 'settingSVGs')
        self.$store.dispatch('setBackdropMessage', Translate.translate(self.$store.getters.orderState))

        // Now, let's save all our pictures.
        self.$store.dispatch('setSVGs', order).then(function (response) {
          if (self.isQliroEnabled) {
            self.$store.dispatch('createQliroOrder', order.id).then(function (response) {
              self.$store.dispatch('showBackdrop', false)
              self.qliroSnippet = response.body.OrderHtmlSnippet
              self.$nextTick(() => {
                self.initQliroDiv()
              })
            }).catch(function (response) {
              self.$store.dispatch('showBackdrop', false)
              self.handleOrderError(response.body, 'createQliroOrder')
            })
          } else {
            if (self.$store.getters.paymentAmount > 0) {
              // Now that the order was successfully created, set up the object we'll provide to Stripe to get a stripe token.
              let stripeSourcePayload = {
                billing_details: {
                  name: order.customer.name,
                  email: order.customer.email,
                  phone: order.customer.phone,
                  address: {
                    line1: order.shipping.address.street,
                    city: order.shipping.address.city,
                    postal_code: order.shipping.address.postalCode,
                    state: order.shipping.address.state,
                    country: order.shipping.address.country
                  }
                }
              }
              self.$store.dispatch('setOrderState', 'creatingSource')
              self.$store.dispatch('setBackdropMessage', Translate.translate(self.$store.getters.orderState))
              stripe.createPaymentMethod('card', card, stripeSourcePayload).then(function (source) {
                if (source.paymentMethod) {
                  self.$store.dispatch('setOrderState', 'processingPayment')
                  self.$store.dispatch('setBackdropMessage', Translate.translate(self.$store.getters.orderState))
                  self.$store.dispatch('payOrder', {
                    order,
                    paymentMethod: source.paymentMethod,
                    amount: self.$store.getters.paymentAmount,
                    currency: self.$store.getters.currency.currency
                  }).then(function (payOrderResponse) {
                    if (payOrderResponse.body.requiresAction && payOrderResponse.body.requiresAction === true) {
                      stripe.handleCardAction(payOrderResponse.body.paymentIntentClientSecret).then(function (cardAction) {
                        if (cardAction.paymentIntent) {
                          self.$store.dispatch('payOrder', {
                            order,
                            paymentIntent: cardAction.paymentIntent,
                            amount: self.$store.getters.paymentAmount,
                            currency: self.$store.getters.currency.currency
                          }).then(function (confirmPaymentResponse) {
                            self.$store.dispatch('setOrderPaymentSourceLast4', confirmPaymentResponse.body.paymentSource.last4)
                            self.$store.dispatch('setOrderPaymentSourceBrand', confirmPaymentResponse.body.paymentSource.brand)
                            self.$store.dispatch('trackOrderConfirmed', order)
                            self.$store.dispatch('setAllowClosingBackdrop', true)
                            self.$store.dispatch('showBackdrop', false)
                            self.$router.push({ name: 'checkout-confirmation' })
                            // self.$store.dispatch('googleCustomerReviews', googleCustomerReviewsData)
                          }).catch(function (response) { self.handleOrderError(response.body, 'confirmPayment') })
                        } else {
                          self.$store.dispatch('showBackdrop', false)
                          self.handleOrderError(cardAction, 'handleCardAction')
                        }
                      }).catch(function (response) {
                        self.$store.dispatch('showBackdrop', false)
                        self.handleOrderError(response, 'handleCardAction')
                      })
                    } else {
                      self.$store.dispatch('setOrderPaymentSourceLast4', payOrderResponse.body.paymentSource.last4)
                      self.$store.dispatch('setOrderPaymentSourceBrand', payOrderResponse.body.paymentSource.brand)
                      self.$store.dispatch('trackOrderConfirmed', order)
                      self.$store.dispatch('setAllowClosingBackdrop', true)
                      self.$store.dispatch('showBackdrop', false)
                      self.$router.push({ name: 'checkout-confirmation' })
                      // self.$store.dispatch('googleCustomerReviews', googleCustomerReviewsData)
                    }
                  }).catch(function (response) { self.handleOrderError(response.body, 'payOrder') })
                } else {
                  self.$store.dispatch('showBackdrop', false)
                  self.handleOrderError(source, 'getSource')
                }
              }).catch(function (response) { self.handleOrderError(response, 'getSource') })
            } else {
              self.$store.dispatch('payOrder', { order }).then(function (response) {
                self.$store.dispatch('trackOrderConfirmed', order)
                self.$store.dispatch('setAllowClosingBackdrop', true)
                self.$store.dispatch('showBackdrop', false)
                self.$router.push({ name: 'checkout-confirmation' })
                // self.$store.dispatch('googleCustomerReviews', googleCustomerReviewsData)
              }).catch(function (response) { self.handleOrderError(response.body, 'payOrder') })
            }
          }
        }).catch(function (response) { self.handleOrderError(response, 'setSVGs') })
      }).catch(function (response) { self.handleOrderError(response, 'createOrder') })
    },

    updateOrder () {
      this.errorMessage = ''
      let self = this
      this.$store.dispatch('setAllowClosingBackdrop', false)
      this.$store.dispatch('setOrderState', 'updatingOrder')
      this.$store.dispatch('setBackdropMessage', Translate.translate(this.$store.getters.orderState))
      this.$store.dispatch('showBackdrop', true)
      this.updateOrderPromise().then(function () {
        self.$store.dispatch('showBackdrop', false)
        if (typeof q1 !== 'undefined') {
          q1.lock()
          q1.onOrderUpdated(function (order) {
            if (order.totalPrice === self.$store.getters.grandTotal / 100) {
              q1.unlock()
            }
          })
        }
      }).catch(function (response) { self.handleOrderError(response, 'updateOrder') })
    },

    updateOrderPromise () {
      let self = this
      return new Promise((resolve, reject) => {
        this.$store.dispatch('updateOrder').then(function (response) {
          const order = response.body
          Promise.all([
            self.$store.dispatch('updateQliroOrder'),
            self.$store.dispatch('setSVGs', order)
          ]).then(function (result) {
            resolve([order, result[0], result[1]])
          }).catch((err) => {
            reject(err)
          })
        }).catch((err) => {
          reject(err)
        })
      })
    },

    initQliroDiv () {
      const qliroDiv = document.getElementById('qliroDiv')
      if (qliroDiv) {
        const scripts = qliroDiv.getElementsByTagName('script')
        const newScripts = []
        for (let i = 0; i < scripts.length; i += 1) {
          if (scripts[i].innerHTML !== '') {
            // eslint-disable-next-line no-eval
            eval(scripts[i].innerHTML)
          }
          if (scripts[i].src !== '') {
            const po = document.createElement('script')
            po.type = 'text/javascript'
            po.async = true
            po.src = scripts[i].src
            newScripts.push(po)
          }
        }
        for (let i = 0; i < newScripts.length; i += 1) {
          qliroDiv.appendChild(newScripts[i])
        }
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/checkout/script.js